import { useEffect, useRef } from "react";
import { AppState, Platform } from "react-native";
import { Image as ExpoImage } from "expo-image";
import { useQueryClient } from "@tanstack/react-query";

const CHECK_INTERVAL_MS = 10_000;

/**
 * Periodically clears image and query caches on iOS to reduce memory pressure.
 *
 * Clears expo-image memory cache on every interval tick.
 * Performs a full cleanup (image memory + disk + inactive queries) whenever
 * the app returns from background, where iOS may have reclaimed memory.
 */
export function useMemoryPressure() {
  const queryClient = useQueryClient();
  const lastCleanup = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (Platform.OS !== "ios") return;

    const clearMemoryCache = async () => {
      try {
        await ExpoImage.clearMemoryCache();
      } catch (e) {
        console.error(
          "[MemPressure] ❌ Failed to clear image memory cache:",
          e
        );
      }
    };

    const clearAllCaches = async () => {
      try {
        await ExpoImage.clearMemoryCache();
        await ExpoImage.clearDiskCache();

        const cache = queryClient.getQueryCache();
        const allQueries = cache.getAll();
        const inactive = allQueries.filter((q) => q.getObserversCount() === 0);
        for (const q of inactive) {
          cache.remove(q);
        }
      } catch (e) {
        console.error("[MemPressure] ❌ CRITICAL cleanup failed:", e);
      }
    };

    const runPeriodicCleanup = async () => {
      const now = Date.now();
      if (now - lastCleanup.current < 5000) {
        return;
      }
      lastCleanup.current = now;
      await clearMemoryCache();
    };

    intervalRef.current = setInterval(runPeriodicCleanup, CHECK_INTERVAL_MS);

    const subscription = AppState.addEventListener("change", (nextState) => {
      if (nextState === "active") {
        clearAllCaches();
      }
    });

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      subscription.remove();
    };
  }, [queryClient]);
}
